import React, { useState, useEffect } from "react";

export const DeviceList = ({
    devices = [],
    selectedDevice,
    onDeviceSelect,
    onDevicePlay,
    playingUrl,
    selectedSubDeviceUrl,
    onSubDeviceSelect,
    collapsed = false,
    onToggleCollapsed,
}) => {
    const [expandedIndex, setExpandedIndex] = useState(null);

    useEffect(() => {
        if (!selectedDevice) return;
        const idx = devices.findIndex((d) => d.url === selectedDevice.url);
        if (
            idx !== -1 &&
            Array.isArray(devices[idx].subDevices) &&
            devices[idx].subDevices.length > 0
        ) {
            setExpandedIndex(idx);
        }
    }, [selectedDevice, devices]);

    const extractDeviceName = (url) => {
        try {
            const u = new URL(url);
            const parts = u.pathname.split("/").filter(Boolean);
            return parts[parts.length - 1] || u.hostname;
        } catch {
            return url;
        }
    };

    const cardClass = (isSelected) =>
        `card relative p-4 rounded-lg transition-shadow transition-colors ${
            isSelected ? "border-2 border-primary" : "shadow-lg hover:shadow-xl"
        }`;

    const ToggleButton = ({ compact = false }) => (
        <button
            type="button"
            onClick={onToggleCollapsed}
            className={`rounded-lg border border-border bg-background text-foreground hover:bg-accent transition-colors flex items-center justify-center ${
                compact ? "w-10 h-10" : "w-9 h-9"
            }`}
            title={collapsed ? "Show devices" : "Hide devices"}
            aria-label={collapsed ? "Show devices" : "Hide devices"}
        >
            <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d={collapsed ? "M9 6l6 6-6 6" : "M15 6l-6 6 6 6"}
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </button>
    );

    if (collapsed) {
        return (
            <aside className="w-16 min-w-[64px] h-full bg-muted p-3 box-border flex flex-col items-center gap-4 border-r border-border">
                <ToggleButton compact />
                <div className="w-10 h-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-extrabold shadow-md">
                    {devices.length}
                </div>
                <div
                    className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-extrabold"
                    style={{
                        writingMode: "vertical-rl",
                        textOrientation: "mixed",
                    }}
                >
                    Devices
                </div>
            </aside>
        );
    }

    return (
        <aside className="w-80 min-w-[280px] h-full bg-muted p-5 box-border overflow-y-auto app-scrollbar border-r border-border">
            <div className="card p-4 rounded-lg shadow-md mb-6">
                <div className="flex items-center justify-between gap-3">
                    <h3 className="text-2xl font-bold text-center m-0 text-foreground flex-1">
                        Devices
                    </h3>
                    <ToggleButton />
                </div>

                {devices.length === 0 && (
                    <div className="text-muted-foreground">No devices</div>
                )}
            </div>

            <div className="flex flex-col gap-4">
                {devices.map((device, idx) => {
                    const isSelected =
                        selectedDevice && selectedDevice.url === device.url;
                    const deviceName =
                        device.label ||
                        extractDeviceName(device.url || `device-${idx}`);
                    const hasSub =
                        Array.isArray(device.subDevices) &&
                        device.subDevices.length > 0;
                    const expanded = expandedIndex === idx;

                    return (
                        <div
                            key={device.url || idx}
                            className={cardClass(isSelected)}
                        >
                            {hasSub && (
                                <div
                                    className="absolute top-3 right-3 w-9 h-9 rounded-md flex items-center justify-center bg-muted border border-border cursor-pointer hover:bg-accent transition-colors"
                                    onClick={() =>
                                        setExpandedIndex(expanded ? null : idx)
                                    }
                                    title={expanded ? "Collapse" : "Expand"}
                                >
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d={
                                                expanded
                                                    ? "M6 15l6-6 6 6"
                                                    : "M6 9l6 6 6-6"
                                            }
                                            stroke="currentColor"
                                            strokeWidth="1.6"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="text-foreground"
                                        />
                                    </svg>
                                </div>
                            )}

                            <div className="flex justify-between items-center">
                                <div
                                    onClick={() => {
                                        if (onDeviceSelect)
                                            onDeviceSelect(device);
                                    }}
                                    className="cursor-pointer min-w-0 pr-14"
                                >
                                    <div className="font-extrabold text-foreground text-[15px] leading-tight">
                                        {deviceName}
                                    </div>
                                    {device.layout === "quad" && (
                                        <div
                                            className="inline-flex mt-2 px-2 py-0.5 rounded-full text-[11px] font-bold"
                                            style={{
                                                background:
                                                    "var(--color-secondary)",
                                                color: "var(--color-white)",
                                            }}
                                        >
                                            4 streams
                                        </div>
                                    )}
                                    <div className="text-sm text-muted-foreground mt-1 break-words">
                                        {device.url}
                                    </div>
                                </div>
                            </div>

                            {hasSub && expanded && (
                                <div className="mt-3 flex flex-col gap-2">
                                    {device.subDevices.map((s) => {
                                        const norm = (u) =>
                                            typeof u === "string"
                                                ? u.replace(/\/+$/, "")
                                                : u;
                                        const isSubSelected =
                                            norm(selectedSubDeviceUrl) ===
                                            norm(s.url);
                                        const subClass = isSubSelected
                                            ? "card p-2.5 rounded-md border-2 border-primary shadow-md cursor-pointer flex flex-col gap-1 transition-transform transform hover:-translate-y-0.5"
                                            : "card p-2.5 rounded-md cursor-pointer flex flex-col gap-1 transition-shadow hover:shadow-md";
                                        return (
                                            <div
                                                key={s.id || s.url}
                                                onClick={() => {
                                                    // Selecting a subdevice stops any current playback and shows the new device without playing
                                                    if (onSubDeviceSelect)
                                                        onSubDeviceSelect(
                                                            s.url,
                                                            device,
                                                            s.label || s.id,
                                                        );
                                                }}
                                                role="button"
                                                tabIndex={0}
                                                className={subClass}
                                            >
                                                <div className="text-sm font-semibold text-foreground">
                                                    {s.label ||
                                                        s.id ||
                                                        "stream"}
                                                </div>
                                                <div className="text-xs text-muted-foreground break-words">
                                                    {s.url}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </aside>
    );
};

export default DeviceList;
